import { z } from "zod";

export const PENDING_SHOPIFY_INTEGRATION_COOKIE =
  "replo_pending_shopify_integration";

export const pendingShopifyIntegrationCookieSchema = z.discriminatedUnion(
  "type",
  [
    z.object({
      type: z.literal("onboarding"),
      projectId: z.string().nullish(),
      workspaceId: z.string().nullish(),
      hostingType: z.enum(["shopify", "replo"]).nullish(),
    }),
    z.object({
      type: z.literal("home"),
      projectId: z.string(),
      workspaceId: z.string(),
    }),
    z.object({
      type: z.literal("sections"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("noShopifyErrorModal"),
      projectId: z.string(),
      workspaceId: z.string(),
    }),
    z.object({
      type: z.literal("projectSettings"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("newProject"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("publish"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("shopStyles"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("preview"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("assets"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("productPicker"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("billingModal"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("shopSettingsIntegration"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("agentIntegrations"),
      projectId: z.string(),
      returnPath: z.string().optional(),
    }),
    z.object({
      type: z.literal("projectView"),
      workspaceId: z.string(),
      projectId: z.string(),
    }),
    z.object({
      type: z.literal("analytics"),
      projectId: z.string(),
      workspaceId: z.string(),
    }),
    z.object({
      type: z.literal("abtesting"),
      projectId: z.string(),
      experimentId: z.string().nullish(),
      workspaceId: z.string().nullish(),
    }),
  ],
);

export type PendingShopifyIntegrationCookie = z.infer<
  typeof pendingShopifyIntegrationCookieSchema
>;

export const REPLO_JWT_COOKIE = "reploJwt";
export const REPLO_SESSION_JWT_COOKIE = "replo_session";

/**
 * JS-readable apex-scoped hint ("this browser has a Replo account") the
 * marketing site reads to swap its nav CTA. Never trust it for authorization.
 */
export const REPLO_ACCOUNT_HINT_COOKIE = "replo_account";
export const REPLO_INTERNAL_API_JWT_COOKIE = "reploInternalApiJwt";

// Note (2026-06-09): Set server-side during the Shopify postinstall redirect, this is a
// short-lived backup for the signup `nonce` query param (which can be dropped between the
// redirect and form submission). If present at signup we attribute the user to Shopify
// billing. The wire name is intentionally opaque; do not rename the value (would orphan
// live sessions). The descriptive constant name is the documentation.
export const SHOPIFY_INSTALL_INTENT_COOKIE = "_rsii";

/**
 * Set by bedrock's Shopify reauthorize bounce before sending the merchant to
 * Shopify OAuth. Publisher's update-scopes callback reads it to land them back
 * on the project that started the flow (one shop can back several projects).
 */
export const SHOPIFY_REAUTHORIZE_COOKIE = "replo_shopify_reauthorize";

export const shopifyReauthorizeCookieSchema = z.object({
  projectId: z.uuid(),
});

export type ShopifyReauthorizeCookie = z.infer<
  typeof shopifyReauthorizeCookieSchema
>;

/**
 * Shared Domain= for cookies that must cross bedrock.* ↔ publisher.* on the
 * replo.app apex. Localhost / named workers / staging publisher hosts omit it.
 */
export function getReploRootCookieDomain(hostname: string): string | undefined {
  if (hostname === "replo.app" || hostname.endsWith(".replo.app")) {
    return "replo.app";
  }
  return undefined;
}
